import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Box,
  Text,
  Badge,
  Button,
  Input,
  Textarea,
  Spinner,
  useToast,
  useDisclosure,
  Divider,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
  Link,
  Tooltip,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Collapse,
  Checkbox,
} from "@chakra-ui/react";
import {
  ExternalLinkIcon,
  WarningIcon,
  CheckIcon,
  RepeatIcon,
  ChatIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowForwardIcon,
  ViewIcon,
  CopyIcon,
  InfoOutlineIcon,
  CalendarIcon,
  EditIcon,
} from "@chakra-ui/icons";
import { getSupabase, supabaseSchema } from "./supabaseClient";
import { BASE_URLS, prompts, FETCH_PERIOD_DAYS } from "./env";
import { dbForRow } from "../utils/rowSchema";
import { confirmBadNumbers } from "../utils/badNumbers";
import { formatPhone } from "../utils/failedNumbers";
import {
  isFromPatientMessage as isPatientAuthoredMessage,
  isNewestUnreadMessage,
  shouldReviewCachedMessage,
  shouldReviewFetchedMessage,
} from "../utils/patientMessageSemantics";
import PhoneStatusBadge from "./sms/PhoneStatusBadge";
import PatientPhones from "./sms/PatientPhones";
import { resolveSmsPhone, rowPhones, rowPhonesNormalized } from "../utils/smsPhone";
import { onNumberStatusChange } from "../utils/numberStatus";
import PatientMessageSystemEvent, {
  isPatientMessageSystemEvent,
} from "./PatientMessageSystemEvent";

// Normalize phone number to 10 digits (strip all non-digits, remove leading 1)
// Defined outside component for stable reference
const normalizePhone = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  // If 11 digits starting with 1 (US country code), remove it
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return digits.slice(-10);
};

// The prefetched SMS for every one of the patient's numbers (cell, home —
// AdminPage caches per normalized number), as one thread: each message once,
// oldest first.
const cachedSmsForPhones = (cache, phones) => {
  const seen = new Set();
  const out = [];
  (phones || []).forEach((phone) => {
    (cache?.[phone] || []).forEach((msg) => {
      const key = msg?.id ?? `${msg?.created_at}|${msg?.from}|${msg?.to}|${msg?.message}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(msg);
    });
  });
  return out.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
};

/**
 * PatientMessagingModal - A modal with 3 tabs for patient messaging:
 * 1. Forms Tab - Shows forms sent and submitted
 * 2. Messages Tab - Shows regular SMS messages
 * 3. AI Chat Tab - Shows AI-assisted chat messages with flagging support
 */
const PatientMessagingModal = ({
  isOpen,
  onClose,
  patientData,
  patientId,
  smsCache,
  unreadCount = 0,
  appointmentDate,
  onMessagesViewed,
}) => {
  const toast = useToast();

  // Which of the patient's numbers texts go to (utils/smsPhone resolveSmsPhone):
  // { phone, slot, altPhone, blocked, reason } once resolved, else null.
  const [smsTarget, setSmsTarget] = useState(null);

  // State for each tab - default to Messages tab (index 1) instead of Forms tab (index 0)
  const [activeTabIndex, setActiveTabIndex] = useState(1);

  // Forms tab state
  const [formsSent, setFormsSent] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [expandedForms, setExpandedForms] = useState({});

  // Messages tab state
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [lastRefreshedForms, setLastRefreshedForms] = useState(
    () => new Date(),
  );
  const [lastRefreshedMessages, setLastRefreshedMessages] = useState(
    () => new Date(),
  );
  const [lastRefreshedAi, setLastRefreshedAi] = useState(() => new Date());

  // AI Chat tab state
  const [aiMessages, setAiMessages] = useState([]);
  const [loadingAiMessages, setLoadingAiMessages] = useState(false);
  const [flaggedMessages, setFlaggedMessages] = useState([]);

  // Categorized messages by appointment date
  const [categorizedForms, setCategorizedForms] = useState({
    categorized: {},
    uncategorized: [],
  });
  const [categorizedMessages, setCategorizedMessages] = useState({
    categorized: {},
    uncategorized: [],
  });
  const [categorizedAiMessages, setCategorizedAiMessages] = useState({
    categorized: {},
    uncategorized: [],
  });
  const [loadingCategorization, setLoadingCategorization] = useState(false);

  // Expanded appointment sections state (by tab: forms, messages, ai)
  const [expandedAppointments, setExpandedAppointments] = useState({
    forms: {},
    messages: {},
    ai: {},
  });

  // Message Details modal state
  const {
    isOpen: isMessageDetailsOpen,
    onOpen: onMessageDetailsOpen,
    onClose: onMessageDetailsClose,
  } = useDisclosure();
  const [viewingMessage, setViewingMessage] = useState(null);
  const [copied, setCopied] = useState(false);

  // Resend Modal state for failed messages
  const {
    isOpen: isResendModalOpen,
    onOpen: onResendModalOpen,
    onClose: onResendModalClose,
  } = useDisclosure();
  const [resendMessageData, setResendMessageData] = useState(null);
  const [resendSplitMessage, setResendSplitMessage] = useState(false);

  // Refs for scrolling and input
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const aiMessagesEndRef = useRef(null);
  const aiMessagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const scrollBtnRef = useRef(null);
  const aiScrollBtnRef = useRef(null);

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToAiBottom = () => {
    if (aiMessagesEndRef.current) {
      aiMessagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Check if scrolled to bottom — direct DOM manipulation, no React state
  const handleScroll = () => {
    if (!messagesContainerRef.current || !scrollBtnRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } =
      messagesContainerRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    scrollBtnRef.current.style.opacity = atBottom ? "0" : "1";
    scrollBtnRef.current.style.pointerEvents = atBottom ? "none" : "auto";
  };

  const handleAiScroll = () => {
    if (!aiMessagesContainerRef.current || !aiScrollBtnRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } =
      aiMessagesContainerRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    aiScrollBtnRef.current.style.opacity = atBottom ? "0" : "1";
    aiScrollBtnRef.current.style.pointerEvents = atBottom ? "none" : "auto";
  };

  // The row backing this modal, unwrapped from the array shape some callers
  // pass. Cross-schema fan-outs (the Providers dropdown, patient
  // suggestions, the roster) tag every merged row with `_schema` — this is
  // the load-bearing read for every SMS query below, since the "SMS" table
  // is per-schema and a patient pulled in from another selected provider
  // does not live in the viewer's own schema.
  const getPatientRow = () => {
    if (!patientData) return null;
    return Array.isArray(patientData) ? patientData[0] : patientData;
  };

  // Every number on file for the patient — cell, then home (utils/smsPhone).
  const getPatientPhones = () => rowPhones(getPatientRow());

  // The number texts go to: the cell/home rule's pick once it has resolved
  // (smsTarget), else the primary number. Raw: for display and for `to`.
  const getPatientPhone = () => smsTarget?.phone || getPatientPhones()[0] || null;

  // 10-digit forms of every number, for matching SMS from/to.
  const getNormalizedPhones = () => [...rowPhonesNormalized(getPatientRow())];

  // Get normalized phone for queries (10 digits) — the primary number
  const getNormalizedPhone = () => normalizePhone(getPatientPhones()[0]);

  // Whether an SMS from/to value is one of the patient's numbers.
  const isPatientNumber = (value) => {
    const n = normalizePhone(value);
    return Boolean(n) && getNormalizedPhones().includes(n);
  };

  // PostgREST `.or()` filter matching any of the patient's numbers on the
  // given columns: "from.ilike.%3105551111,to.ilike.%3105551111,..."
  const phoneOrFilter = (columns) =>
    getNormalizedPhones()
      .flatMap((n) => columns.map((col) => `${col}.ilike.%${n}`))
      .join(",");

  // Resolve which number texts go to for this patient; re-resolve when the
  // badges re-read sms_number_health (after a send).
  const phonesKey = getPatientPhones().join("|");
  useEffect(() => {
    if (!isOpen) return undefined;
    let live = true;
    const read = () =>
      resolveSmsPhone(getPatientRow()).then((res) => {
        if (live) setSmsTarget(res);
      });
    read();
    const unsubscribe = onNumberStatusChange(read);
    return () => {
      live = false;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, phonesKey]);

  // Get patient name from patientData
  const getPatientName = () => {
    if (!patientData) return "Unknown Patient";
    const patient = Array.isArray(patientData) ? patientData[0] : patientData;
    return (
      patient?.[prompts[0][0]] ||
      patient?.patient_name ||
      patient?.name ||
      "Unknown Patient"
    );
  };

  // Compute date window around the appointment date using FETCH_PERIOD_DAYS
  const getDateRange = () => {
    if (!appointmentDate) return null;
    const base = new Date(appointmentDate + "T12:00:00");
    if (isNaN(base.getTime())) return null;
    const ms = FETCH_PERIOD_DAYS * 24 * 60 * 60 * 1000;
    const from = new Date(base.getTime() - ms);
    const to = new Date(base.getTime() + ms);
    return { from: from.toISOString(), to: to.toISOString() };
  };

  // Get backend base URL
  const getBackendBaseUrl = useCallback(() => {
    const override = process.env.REACT_APP_BACKEND_URL;
    if (override && override.trim() !== "") return override;
    const hostname = window.location.hostname;
    return hostname === "localhost" || hostname === "127.0.0.1"
      ? BASE_URLS.local.backend
      : BASE_URLS.render.backend;
  }, []);

  // Categorize messages by appointment using the backend API
  const categorizeMessagesByAppointment = useCallback(
    async (messagesToCategorize, phoneOverride = null) => {
      // Use phoneOverride if provided, otherwise the patient's primary number
      // (the cell): the backend looks the patient's appointments up by it.
      const patientPhone = phoneOverride || getPatientPhones()[0] || null;
      console.log(
        "[Categorize] Starting categorization for phone:",
        patientPhone,
        "messages:",
        messagesToCategorize?.length,
      );

      if (
        !patientPhone ||
        !messagesToCategorize ||
        messagesToCategorize.length === 0
      ) {
        console.log(
          "[Categorize] Skipping - no phone or no messages. patientData:",
          patientData,
        );
        return { categorized: {}, uncategorized: messagesToCategorize || [] };
      }

      try {
        const backendBaseUrl = getBackendBaseUrl();
        console.log(
          "[Categorize] Calling backend:",
          `${backendBaseUrl}/twilio/categorize-messages/`,
        );
        // A row fanned in from another selected provider lives in ITS
        // schema, not the viewer's — the backend looks up appointment
        // history by schema to categorize by appointment date.
        const rowSchema = getPatientRow()?._schema || supabaseSchema;
        console.log("[Categorize] Request payload:", {
          messages_count: messagesToCategorize.length,
          patient_phone: patientPhone,
          schema: rowSchema,
        });

        const response = await fetch(
          `${backendBaseUrl}/twilio/categorize-messages/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              messages: messagesToCategorize,
              patient_phone: patientPhone,
              schema: rowSchema,
            }),
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "[Categorize] Failed - status:",
            response.status,
            "error:",
            errorText,
          );
          return { categorized: {}, uncategorized: messagesToCategorize };
        }

        const result = await response.json();

        // Log detailed results
        console.log("[Categorize] Response received:");
        console.log("[Categorize] - Status:", result.status);
        console.log(
          "[Categorize] - Total appointments found:",
          result._debug?.total_appointments_found,
        );
        console.log(
          "[Categorize] - Appointment dates:",
          result._debug?.appointment_dates,
        );
        console.log(
          "[Categorize] - Categorized groups:",
          Object.keys(result.categorized || {}).length,
        );
        console.log(
          "[Categorize] - Uncategorized count:",
          result.uncategorized?.length,
        );

        // Log the last 10 scheduled dates from appointments
        if (result.appointments && result.appointments.length > 0) {
          const last10Dates = result.appointments
            .slice(0, 10)
            .map((a) => a.date_);
          console.log(
            "[Categorize] Last 10 scheduled dates for patient:",
            last10Dates,
          );

          // Log first few message dates to compare
          const first5MsgDates = messagesToCategorize
            .slice(0, 5)
            .map((m) => m.created_at);
          console.log("[Categorize] First 5 message dates:", first5MsgDates);
          console.log(
            "[Categorize] Grace period (days):",
            result.grace_period_days,
          );
        } else {
          console.log("[Categorize] No appointments found for this patient!");
        }

        return {
          categorized: result.categorized || {},
          uncategorized: result.uncategorized || [],
        };
      } catch (error) {
        console.error("[Categorize] Error:", error);
        return { categorized: {}, uncategorized: messagesToCategorize };
      }
    },
    [getBackendBaseUrl, patientData],
  );

  // Backfill appointment_date in SMS table for messages that were categorized
  // but don't already have appointment_date set
  const backfillAppointmentDates = useCallback(async (categorized) => {
    if (!categorized || Object.keys(categorized).length === 0) return;

    try {
      const supabase = await getSupabase();

      for (const [appointmentDate, section] of Object.entries(categorized)) {
        const msgs = section.messages || [];
        // Update messages whose appointment_date is null or doesn't match
        const idsToUpdate = msgs
          .filter((msg) => {
            const id = msg.id || msg.part1?.id;
            return id && msg.appointment_date !== appointmentDate;
          })
          .map((msg) => msg.id || msg.part1?.id);

        if (idsToUpdate.length === 0) continue;

        const { error } = await dbForRow(supabase, getPatientRow(), supabaseSchema)
          .from("SMS")
          .update({ appointment_date: appointmentDate })
          .in("id", idsToUpdate);

        if (error) {
          console.error(
            `[Backfill] Error updating appointment_date for ${appointmentDate}:`,
            error,
          );
        } else {
          console.log(
            `[Backfill] Set appointment_date=${appointmentDate} for ${idsToUpdate.length} messages`,
          );
        }
      }
    } catch (error) {
      console.error("[Backfill] Error backfilling appointment dates:", error);
    }
  }, []);

  // Initialize expanded state for appointment sections
  // If appointmentDate prop matches a section, expand that one; otherwise expand the latest
  const initializeExpandedState = useCallback(
    (categorized, tabKey) => {
      const isReversedTab = tabKey === "messages" || tabKey === "ai";
      const dates = Object.keys(categorized).sort((a, b) =>
        isReversedTab ? new Date(a) - new Date(b) : new Date(b) - new Date(a),
      );
      // Check if the current appointmentDate matches one of the sections
      const hasMatch = appointmentDate && dates.includes(appointmentDate);
      const expandedState = {};
      dates.forEach((date, index) => {
        if (hasMatch) {
          expandedState[date] = date === appointmentDate;
        } else {
          // Fallback: expand newest section
          expandedState[date] = isReversedTab
            ? index === dates.length - 1
            : index === 0;
        }
      });
      setExpandedAppointments((prev) => ({
        ...prev,
        [tabKey]: expandedState,
      }));
    },
    [appointmentDate],
  );

  // Toggle appointment section expand/collapse
  const toggleAppointmentSection = (tabKey, appointmentDate) => {
    setExpandedAppointments((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        [appointmentDate]: !prev[tabKey]?.[appointmentDate],
      },
    }));
  };

  // Format appointment date for display (just the date, no time)
  const formatAppointmentDate = (dateString) => {
    if (!dateString) return "Unknown Date";
    const date = new Date(dateString + "T12:00:00"); // Add time to avoid timezone issues
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Fetch forms data - SMS messages containing form links
  const fetchFormsData = useCallback(async () => {
    if (!patientId) return;

    setLoadingForms(true);
    try {
      const supabase = await getSupabase();
      const normalizedPhone = getNormalizedPhone();

      if (!normalizedPhone) {
        console.warn("No patient phone number available");
        setFormsSent([]);
        return;
      }

      // Query SMS messages sent to this patient, sorted by created_at ascending
      const dateRange = getDateRange();
      let query = dbForRow(supabase, getPatientRow(), supabaseSchema)
        .from("SMS")
        .select("*")
        .or(phoneOrFilter(["to"]));
      if (dateRange) {
        query = query
          .gte("created_at", dateRange.from)
          .lte("created_at", dateRange.to);
      }
      const { data, error } = await query.order("created_at", {
        ascending: true,
      });

      if (error) {
        console.error("Error fetching form messages:", error);
        throw error;
      }

      // Helper functions to identify message types
      const isFormPart1 = (msg) => {
        const message = (msg?.message || "").trim();
        // Only match if message STARTS with "Part 1:"
        return message.startsWith("Part 1:");
      };

      const isFormPart2 = (msg) => {
        const message = (msg?.message || "").trim();
        // Only match if message STARTS with "Part 2"
        return message.startsWith("Part 2");
      };

      const hasFormLink = (msg) => {
        const message = msg?.message || "";
        return (
          message.includes("wtf-forms") ||
          message.includes("/form/") ||
          message.includes("forms.") ||
          message.includes("bit.ly") ||
          message.includes("tinyurl") ||
          message.includes("COMPLETE YOUR FORMS")
        );
      };

      // Group Part 1 with following Part 2 messages
      const groupedForms = [];
      const usedPart2Ids = new Set();

      // First pass: find all Part 1 messages and pair with next Part 2
      const allMessages = data || [];

      for (let i = 0; i < allMessages.length; i++) {
        const msg = allMessages[i];

        if (isFormPart1(msg)) {
          // Look for the next Part 2 message after this Part 1
          let part2Msg = null;
          for (let j = i + 1; j < allMessages.length; j++) {
            if (
              isFormPart2(allMessages[j]) &&
              !usedPart2Ids.has(allMessages[j].id)
            ) {
              part2Msg = allMessages[j];
              usedPart2Ids.add(allMessages[j].id);
              break;
            }
            // Stop looking if we hit another Part 1
            if (isFormPart1(allMessages[j])) {
              break;
            }
          }

          groupedForms.push({
            type: "form_group",
            part1: msg,
            part2: part2Msg,
            created_at: msg.created_at,
          });
        }
      }

      // Second pass: add standalone form link messages (not Part 1 or Part 2)
      for (const msg of allMessages) {
        if (!isFormPart1(msg) && !isFormPart2(msg) && hasFormLink(msg)) {
          groupedForms.push({
            type: "standalone",
            part1: msg,
            part2: null,
            created_at: msg.created_at,
          });
        }
      }

      // Sort by created_at descending (newest first)
      groupedForms.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );

      setFormsSent(groupedForms);

      // Categorize forms by appointment date
      // Convert form groups to messages format for categorization
      const formsAsMessages = groupedForms.map((group) => ({
        ...group,
        created_at: group.created_at,
        id: group.part1?.id,
      }));
      // Pass the phone number directly since patientData might not be ready in the callback
      const phoneForCategorization = getNormalizedPhone() || normalizedPhone;
      console.log("[Forms] Categorizing with phone:", phoneForCategorization);
      const categorized = await categorizeMessagesByAppointment(
        formsAsMessages,
        phoneForCategorization,
      );
      setCategorizedForms(categorized);
      initializeExpandedState(categorized.categorized, "forms");
      backfillAppointmentDates(categorized.categorized);
    } catch (error) {
      console.error("Error fetching forms data:", error);
      toast({
        title: "Error",
        description: "Failed to load forms data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingForms(false);
      setLastRefreshedForms(new Date());
    }
  }, [
    patientId,
    toast,
    categorizeMessagesByAppointment,
    initializeExpandedState,
    backfillAppointmentDates,
  ]);

  // Fetch SMS messages when Messages tab is selected
  const fetchMessages = useCallback(async () => {
    if (!patientId) return;

    setLoadingMessages(true);
    try {
      const supabase = await getSupabase();
      const normalizedPhone = getNormalizedPhone();

      if (!normalizedPhone) {
        console.warn("No patient phone number available");
        setMessages([]);
        return;
      }

      // Query using phone pattern matching directly in Supabase
      // This is more efficient than fetching all and filtering client-side

      console.log("[Messages] Querying with phone filter:", phoneOrFilter(["from", "to"]));

      const dateRange = getDateRange();
      let query = dbForRow(supabase, getPatientRow(), supabaseSchema)
        .from("SMS")
        .select("*")
        .or(phoneOrFilter(["from", "to"]));
      if (dateRange) {
        query = query
          .gte("created_at", dateRange.from)
          .lte("created_at", dateRange.to);
      }
      const { data, error } = await query.order("created_at", {
        ascending: true,
      });

      if (error) {
        console.error("Error fetching messages:", error);
        throw error;
      }

      console.log("[Messages] Records fetched:", data?.length);

      // Deduplicate by id (in case of duplicate entries)
      const seen = new Set();
      const deduped = (data || []).filter((msg) => {
        if (seen.has(msg.id)) return false;
        seen.add(msg.id);
        return true;
      });

      console.log("[Messages] After dedup:", deduped.length);
      setMessages(deduped);

      // Mark all incoming messages (from patient) as reviewed when modal opens
      // Use direction='inbound' for new messages, fall back to 'from' field for older messages
      const markMessagesAsReviewed = async () => {
        try {
          const incomingMessages = deduped.filter((msg) =>
            shouldReviewFetchedMessage(msg, (msgFrom) => {
              const from = msgFrom || "";
              return (
                from.includes(normalizedPhone) ||
                from.includes(normalizedPhone.slice(-10))
              );
            }),
          );

          if (incomingMessages.length === 0) return;

          const messageIds = incomingMessages
            .map((msg) => msg.id)
            .filter((id) => id);
          if (messageIds.length === 0) return;

          // Update all incoming messages as reviewed
          const { error: updateError } = await dbForRow(supabase, getPatientRow(), supabaseSchema)
            .from("SMS")
            .update({ reviewed: true })
            .in("id", messageIds);

          if (updateError) {
            console.error("Error marking messages as reviewed:", updateError);
          } else {
            console.log(
              `[Messages] Marked ${messageIds.length} messages as reviewed`,
            );
          }
        } catch (err) {
          console.error("Error in markMessagesAsReviewed:", err);
        }
      };

      // Mark messages as reviewed (don't await - do it in background)
      markMessagesAsReviewed();

      // Categorize messages by appointment date
      // Pass the phone number directly since patientData might not be ready in the callback
      const phoneForCategorization = getNormalizedPhone() || normalizedPhone;
      console.log(
        "[Messages] Categorizing with phone:",
        phoneForCategorization,
      );
      const categorized = await categorizeMessagesByAppointment(
        deduped,
        phoneForCategorization,
      );
      setCategorizedMessages(categorized);
      initializeExpandedState(categorized.categorized, "messages");
      backfillAppointmentDates(categorized.categorized);
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast({
        title: "Error",
        description: "Failed to load messages",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingMessages(false);
      setLastRefreshedMessages(new Date());
    }
  }, [
    patientId,
    toast,
    categorizeMessagesByAppointment,
    initializeExpandedState,
    backfillAppointmentDates,
  ]);

  // Fetch AI chat messages when AI Chat tab is selected
  const fetchAiMessages = useCallback(async () => {
    if (!patientId) return;

    setLoadingAiMessages(true);
    try {
      const supabase = await getSupabase();
      const normalizedPhone = getNormalizedPhone();

      if (!normalizedPhone) {
        console.warn("No patient phone number available");
        setAiMessages([]);
        setFlaggedMessages([]);
        return;
      }

      // Query using phone pattern matching directly in Supabase

      const dateRange = getDateRange();
      let query = dbForRow(supabase, getPatientRow(), supabaseSchema)
        .from("SMS")
        .select("*")
        .eq("is_ai_chat", true)
        .or(phoneOrFilter(["from", "to"]));
      if (dateRange) {
        query = query
          .gte("created_at", dateRange.from)
          .lte("created_at", dateRange.to);
      }
      const { data, error } = await query.order("created_at", {
        ascending: true,
      });

      if (error) {
        console.error("Error fetching AI messages:", error);
        throw error;
      }

      // Deduplicate by id (in case of duplicate entries)
      const seen = new Set();
      const deduped = (data || []).filter((msg) => {
        if (seen.has(msg.id)) return false;
        seen.add(msg.id);
        return true;
      });

      setAiMessages(deduped);

      // Filter flagged messages
      const flagged = deduped.filter((msg) => msg.ai_flagged === true);
      setFlaggedMessages(flagged);

      // Categorize AI messages by appointment date
      // Pass the phone number directly since patientData might not be ready in the callback
      const phoneForCategorization = getNormalizedPhone() || normalizedPhone;
      console.log(
        "[AI Messages] Categorizing with phone:",
        phoneForCategorization,
      );
      const categorized = await categorizeMessagesByAppointment(
        deduped,
        phoneForCategorization,
      );
      setCategorizedAiMessages(categorized);
      initializeExpandedState(categorized.categorized, "ai");
      backfillAppointmentDates(categorized.categorized);
    } catch (error) {
      console.error("Error fetching AI messages:", error);
      toast({
        title: "Error",
        description: "Failed to load AI chat messages",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingAiMessages(false);
      setLastRefreshedAi(new Date());
    }
  }, [
    patientId,
    toast,
    categorizeMessagesByAppointment,
    initializeExpandedState,
    backfillAppointmentDates,
  ]);

  // Send a regular SMS message
  const sendMessage = async () => {
    const messageText = messageInputRef.current?.value?.trim() || "";
    if (!messageText) return;

    const patientPhone = getPatientPhone();
    if (!patientPhone) {
      toast({
        title: "Error",
        description: "Patient phone number not available",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const badNumberGuard = await confirmBadNumbers([{ phone: patientPhone }]);
    if (!badNumberGuard) return;

    setSendingMessage(true);
    try {
      const backendBaseUrl = getBackendBaseUrl();

      const response = await fetch(`${backendBaseUrl}/twilio/send-sms/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          to: patientPhone,
          message: messageText,
          patientId: patientId,
          confirm_bad_number: badNumberGuard.confirmed(patientPhone),
          // A patient fanned in from another selected provider lives in ITS
          // schema, not the viewer's — sending with `supabaseSchema` would
          // write the outbound text (and pick a sender number) for the
          // wrong clinic, and the reply would never show up here.
          schema: getPatientRow()?._schema || supabaseSchema,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (
          errorData.code === "DNT_BLOCKED" ||
          errorData.error?.includes("Do Not Text")
        ) {
          toast({
            title: "Message Blocked",
            description:
              "Message cannot be sent to this recipient! They are on the do not text list.",
            status: "warning",
            duration: 8000,
            isClosable: true,
          });
          return;
        }
        throw new Error(errorData.error || "Failed to send message");
      }

      toast({
        title: "Message Sent",
        description: "Your message has been sent successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Clear the input
      if (messageInputRef.current) {
        messageInputRef.current.value = "";
      }
      // Refresh messages list
      fetchMessages();
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send message",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingMessage(false);
    }
  };

  // Resend a message. `badNumberGuard` lets a caller sending several parts
  // ask about a bad number once instead of once per part.
  const resendMessage = async (messageText, split = false, badNumberGuard) => {
    if (!messageText?.trim()) return;

    const patientPhone = getPatientPhone();
    if (!patientPhone) {
      toast({
        title: "Error",
        description: "Patient phone number not available",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const guard = badNumberGuard || (await confirmBadNumbers([{ phone: patientPhone }]));
    if (!guard) return;

    setSendingMessage(true);
    try {
      const backendBaseUrl = getBackendBaseUrl();

      const response = await fetch(`${backendBaseUrl}/twilio/send-sms/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          to: patientPhone,
          message: messageText,
          patientId: patientId,
          schema: getPatientRow()?._schema || supabaseSchema,
          split: split,
          confirm_bad_number: guard.confirmed(patientPhone),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (
          errorData.code === "DNT_BLOCKED" ||
          errorData.error?.includes("Do Not Text")
        ) {
          toast({
            title: "Message Blocked",
            description:
              "Message cannot be sent to this recipient! They have opted out of text communications.",
            status: "warning",
            duration: 8000,
            isClosable: true,
          });
          return;
        }
        throw new Error(errorData.error || "Failed to resend message");
      }

      toast({
        title: "Message Resent",
        description: "Your message has been resent successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Refresh messages list
      fetchMessages();
    } catch (error) {
      console.error("Error resending message:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to resend message",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingMessage(false);
    }
  };

  // Mark a flagged message as reviewed
  const markAsReviewed = async (messageId) => {
    try {
      const supabase = await getSupabase();

      const { error } = await dbForRow(supabase, getPatientRow(), supabaseSchema)
        .from("SMS")
        .update({ ai_flagged: false, ai_flag_reason: "Reviewed by doctor" })
        .eq("id", messageId);

      if (error) throw error;

      toast({
        title: "Marked as Reviewed",
        description: "The flagged message has been marked as reviewed",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Refresh AI messages
      fetchAiMessages();
    } catch (error) {
      console.error("Error marking as reviewed:", error);
      toast({
        title: "Error",
        description: "Failed to mark message as reviewed",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle clicking on error preview to open message details modal
  const handleViewMessageDetails = (message) => {
    setViewingMessage(message);
    setCopied(false);
    onMessageDetailsOpen();
  };

  // Copy message content to clipboard
  const handleCopyMessage = async () => {
    if (!viewingMessage) return;

    const messageText = viewingMessage.message || "";

    try {
      await navigator.clipboard.writeText(messageText);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Message content copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast({
        title: "Copy Failed",
        description: "Failed to copy message to clipboard",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Check if a message has failed status
  const isMessageFailed = (msg) => {
    const status = (msg?.status || "").toLowerCase();
    return status === "failed" || status === "undelivered";
  };

  // Render error preview for failed messages
  const renderErrorPreview = (msg) => {
    if (!isMessageFailed(msg)) return null;

    return (
      <Tooltip label="Click to view error details" hasArrow>
        <Box
          cursor="pointer"
          onClick={() => handleViewMessageDetails(msg)}
          _hover={{ opacity: 0.8 }}
          bg="red.50"
          p={2}
          borderRadius="md"
          borderWidth="1px"
          borderColor="red.200"
          mt={1}
        >
          <HStack spacing={1}>
            <InfoOutlineIcon color="red.500" boxSize={3} />
            <Text fontSize="xs" color="red.600" fontWeight="medium">
              Error Details
            </Text>
          </HStack>
          {msg.error_code && (
            <Text fontSize="xs" color="red.500">
              Code: {msg.error_code}
            </Text>
          )}
          <Text fontSize="xs" color="red.600" noOfLines={2}>
            {msg.error_message || "Delivery failed - click for details"}
          </Text>
        </Box>
      </Tooltip>
    );
  };

  // Cache of fully-processed results per phone (survives close/reopen)
  const processedCacheRef = useRef({});

  // Save current state into the processed cache for this phone
  const saveToProcessedCache = useCallback((phone, data) => {
    processedCacheRef.current[phone] = data;
    console.log("[Cache] Saved processed results for", phone);
  }, []);

  // Process cached SMS data into forms/messages/ai categories (client-side)
  // Called only on the FIRST open for a patient; results are then cached.
  const processCachedSMS = useCallback(
    async (cachedMessages) => {
      const phone = getNormalizedPhone();
      if (!phone) return;

      // --- Forms processing (mirrors fetchFormsData logic) ---
      setLoadingForms(true);
      let formsResult, catFormsResult;
      try {
        const toPatient = cachedMessages.filter((msg) => {
          const msgTo = msg.to || "";
          return getNormalizedPhones().some((n) => msgTo.includes(n));
        });

        const isFormPart1 = (msg) => {
          const message = (msg?.message || "").trim();
          return message.startsWith("Part 1:");
        };
        const isFormPart2 = (msg) => {
          const message = (msg?.message || "").trim();
          return message.startsWith("Part 2");
        };
        const hasFormLink = (msg) => {
          const message = msg?.message || "";
          return (
            message.includes("wtf-forms") ||
            message.includes("/form/") ||
            message.includes("forms.") ||
            message.includes("bit.ly") ||
            message.includes("tinyurl") ||
            message.includes("COMPLETE YOUR FORMS")
          );
        };

        const groupedForms = [];
        const usedPart2Ids = new Set();

        for (let i = 0; i < toPatient.length; i++) {
          const msg = toPatient[i];
          if (isFormPart1(msg)) {
            let part2Msg = null;
            for (let j = i + 1; j < toPatient.length; j++) {
              if (
                isFormPart2(toPatient[j]) &&
                !usedPart2Ids.has(toPatient[j].id)
              ) {
                part2Msg = toPatient[j];
                usedPart2Ids.add(toPatient[j].id);
                break;
              }
              if (isFormPart1(toPatient[j])) break;
            }
            groupedForms.push({
              type: "form_group",
              part1: msg,
              part2: part2Msg,
              created_at: msg.created_at,
            });
          }
        }

        for (const msg of toPatient) {
          if (!isFormPart1(msg) && !isFormPart2(msg) && hasFormLink(msg)) {
            groupedForms.push({
              type: "standalone",
              part1: msg,
              part2: null,
              created_at: msg.created_at,
            });
          }
        }

        groupedForms.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at),
        );
        setFormsSent(groupedForms);
        formsResult = groupedForms;

        const formsAsMessages = groupedForms.map((group) => ({
          ...group,
          created_at: group.created_at,
          id: group.part1?.id,
        }));
        const categorized = await categorizeMessagesByAppointment(
          formsAsMessages,
          phone,
        );
        setCategorizedForms(categorized);
        catFormsResult = categorized;
        initializeExpandedState(categorized.categorized, "forms");
        backfillAppointmentDates(categorized.categorized);
      } catch (err) {
        console.error("[Cache] Error processing forms:", err);
      } finally {
        setLoadingForms(false);
      }

      // --- Messages processing (mirrors fetchMessages logic) ---
      setLoadingMessages(true);
      let messagesResult, catMessagesResult;
      try {
        const seen = new Set();
        const deduped = cachedMessages.filter((msg) => {
          if (seen.has(msg.id)) return false;
          seen.add(msg.id);
          return true;
        });
        setMessages(deduped);
        messagesResult = deduped;

        // Mark incoming messages as reviewed (background)
        (async () => {
          try {
            const supabase = await getSupabase();
            const incomingMessages = deduped.filter((msg) =>
              shouldReviewCachedMessage(msg, (msgFrom) => {
                const from = msgFrom || "";
                return getNormalizedPhones().some((n) => from.includes(n));
              }),
            );
            if (incomingMessages.length === 0) return;
            const messageIds = incomingMessages
              .map((msg) => msg.id)
              .filter((id) => id);
            if (messageIds.length === 0) return;
            await dbForRow(supabase, getPatientRow(), supabaseSchema)
              .from("SMS")
              .update({ reviewed: true })
              .in("id", messageIds);
          } catch (e) {
            console.error("[Cache] Error marking reviewed:", e);
          }
        })();

        const categorized = await categorizeMessagesByAppointment(
          deduped,
          phone,
        );
        setCategorizedMessages(categorized);
        catMessagesResult = categorized;
        initializeExpandedState(categorized.categorized, "messages");
        backfillAppointmentDates(categorized.categorized);
      } catch (err) {
        console.error("[Cache] Error processing messages:", err);
      } finally {
        setLoadingMessages(false);
      }

      // --- AI Messages processing (mirrors fetchAiMessages logic) ---
      setLoadingAiMessages(true);
      let aiResult, flaggedResult, catAiResult;
      try {
        const aiOnly = cachedMessages.filter((msg) => msg.is_ai_chat === true);
        const seen = new Set();
        const deduped = aiOnly.filter((msg) => {
          if (seen.has(msg.id)) return false;
          seen.add(msg.id);
          return true;
        });
        setAiMessages(deduped);
        aiResult = deduped;
        const flagged = deduped.filter((msg) => msg.ai_flagged === true);
        setFlaggedMessages(flagged);
        flaggedResult = flagged;

        const categorized = await categorizeMessagesByAppointment(
          deduped,
          phone,
        );
        setCategorizedAiMessages(categorized);
        catAiResult = categorized;
        initializeExpandedState(categorized.categorized, "ai");
        backfillAppointmentDates(categorized.categorized);
      } catch (err) {
        console.error("[Cache] Error processing AI messages:", err);
      } finally {
        setLoadingAiMessages(false);
      }

      // Save everything to processed cache so next open is instant
      saveToProcessedCache(phone, {
        formsSent: formsResult,
        categorizedForms: catFormsResult,
        messages: messagesResult,
        categorizedMessages: catMessagesResult,
        aiMessages: aiResult,
        flaggedMessages: flaggedResult,
        categorizedAiMessages: catAiResult,
      });
    },
    [
      categorizeMessagesByAppointment,
      initializeExpandedState,
      saveToProcessedCache,
      backfillAppointmentDates,
    ],
  );

  // Restore fully-processed cached results instantly (no loading, no backend calls)
  const restoreFromProcessedCache = useCallback(
    (cached) => {
      if (cached.formsSent) setFormsSent(cached.formsSent);
      if (cached.categorizedForms) {
        setCategorizedForms(cached.categorizedForms);
        initializeExpandedState(cached.categorizedForms.categorized, "forms");
      }
      if (cached.messages) setMessages(cached.messages);
      if (cached.categorizedMessages) {
        setCategorizedMessages(cached.categorizedMessages);
        initializeExpandedState(
          cached.categorizedMessages.categorized,
          "messages",
        );
      }
      if (cached.aiMessages) setAiMessages(cached.aiMessages);
      if (cached.flaggedMessages) setFlaggedMessages(cached.flaggedMessages);
      if (cached.categorizedAiMessages) {
        setCategorizedAiMessages(cached.categorizedAiMessages);
        initializeExpandedState(cached.categorizedAiMessages.categorized, "ai");
      }
    },
    [initializeExpandedState],
  );

  // Load ALL data when modal opens (not when tab changes)
  useEffect(() => {
    if (!isOpen) return;

    const phone = getNormalizedPhone();

    // 1) Check if we already have fully-processed results for this patient
    if (phone && processedCacheRef.current[phone]) {
      console.log(
        "[PatientMessagingModal] Restoring processed cache for",
        phone,
      );
      restoreFromProcessedCache(processedCacheRef.current[phone]);
      return;
    }

    // 2) Check if we have raw SMS cache — process once, then cache results
    // Texts to or from either of the patient's numbers (cell, home) are
    // cached under each number; merge them into one thread.
    const cached = phone && smsCache ? cachedSmsForPhones(smsCache, getNormalizedPhones()) : [];
    if (cached.length > 0) {
      console.log("[PatientMessagingModal] Processing SMS cache for", phone);
      processCachedSMS(cached);
      return;
    }

    // 3) No cache hit — fall back to original fetch behavior
    fetchFormsData();
    fetchMessages();
    fetchAiMessages();
  }, [
    isOpen,
    fetchFormsData,
    fetchMessages,
    fetchAiMessages,
    smsCache,
    processCachedSMS,
    restoreFromProcessedCache,
  ]);

  // Scroll to the matching appointment section's bottom, or focus on last unread message, or overall bottom as fallback
  useEffect(() => {
    if (messages.length > 0 && activeTabIndex === 1) {
      const scrollBehavior = unreadCount > 0 ? "smooth" : "auto";
      const phoneNorm = getNormalizedPhone();

      const scrollToNewest = () => {
        if (!messagesEndRef.current) return false;
        messagesEndRef.current.scrollIntoView({ behavior: "auto", block: "end" });
        return true;
      };

      const attemptScroll = () => {
        console.log("[Scroll-to-unread] Attempting scroll", {
          unreadCount,
          phoneNorm,
          totalMessages: messages.length,
        });
        // Priority 1: Scroll to the last unread message to bring focus to it
        if (unreadCount > 0 && phoneNorm) {
          // Try to find ANY element with id starting with "unread-msg-"
          // This is more robust than trying to find the "first" unread message
          const unreadEls = document.querySelectorAll('[id^="unread-msg-"]');
          console.log("[Scroll-to-unread] Found unread elements:", unreadEls.length);
          if (unreadEls.length > 0) {
            // Scroll to the first one (which should be the oldest/first unread)
            console.log("[Scroll-to-unread] Scrolling to:", unreadEls[0].id);
            unreadEls[0].scrollIntoView({
              behavior: scrollBehavior,
              block: "center",
            });
            return true;
          } else {
            console.log("[Scroll-to-unread] No unread elements found in DOM");
          }
          // Unread exists but hasn't rendered yet — let the caller retry
          // rather than settling for the bottom on the first pass.
          return false;
        }
        // Priority 2: nothing unread to focus, so open on the NEWEST message.
        // Without this the pane sits at the oldest message and the operator
        // has to scroll the whole history to see what just came in.
        return scrollToNewest();
      };

      // Try immediately first
      if (!attemptScroll()) {
        // Use requestAnimationFrame for better timing with DOM rendering
        requestAnimationFrame(() => {
          if (!attemptScroll()) {
            // Try again after a longer delay
            setTimeout(() => {
              // Last resort: if the unread message never rendered, still open
              // at the newest message instead of stranding the view at the top.
              if (!attemptScroll()) scrollToNewest();
            }, 500);
          }
        });
      }
    }
  }, [messages, activeTabIndex, unreadCount, appointmentDate]);

  // Scroll to the matching appointment section for AI Chat tab
  useEffect(() => {
    if (aiMessages.length > 0 && activeTabIndex === 2) {
      setTimeout(() => {
        if (appointmentDate) {
          const sectionEnd = document.getElementById(
            `ai-section-end-${appointmentDate}`,
          );
          if (sectionEnd) {
            sectionEnd.scrollIntoView({ behavior: "auto", block: "end" });
            return;
          }
        }
        aiMessagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      }, 100);
    }
  }, [aiMessages, activeTabIndex, appointmentDate]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Resend both Part 1 and Part 2 form messages
  const resendFormGroup = async (part1, part2) => {
    const guard = await confirmBadNumbers([{ phone: getPatientPhone() }]);
    if (!guard) return;
    if (part1?.message) {
      await resendMessage(part1.message, false, guard);
    }
    if (part2?.message) {
      // Small delay between messages
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await resendMessage(part2.message, false, guard);
    }
  };

  // Extract URLs from message text
  const extractUrls = (text) => {
    if (!text) return [];
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.match(urlRegex) || [];
  };

  // Render message text with clickable links, emails, phone numbers and preserved newlines
  const renderFormattedText = (text, linkColor = "blue.500") => {
    if (!text) return null;

    // Split by newlines first to preserve line breaks
    const lines = text.split(/\n/);

    return lines.map((line, lineIndex) => {
      // Match URLs, emails, and phone numbers
      // Order matters: URLs first, then emails, then phone numbers
      const tokenRegex =
        /(https?:\/\/[^\s]+)|([a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})|((?:\+?1?\s*[-.]?\s*)?(?:\(?\d{3}\)?)\s*[-.]?\s*\d{3}\s*[-.]?\s*\d{4})/g;

      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = tokenRegex.exec(line)) !== null) {
        // Add text before this match
        if (match.index > lastIndex) {
          parts.push({
            type: "text",
            value: line.slice(lastIndex, match.index),
          });
        }

        if (match[1]) {
          // URL
          parts.push({ type: "url", value: match[1] });
        } else if (match[2]) {
          // Email
          parts.push({ type: "email", value: match[2] });
        } else if (match[3]) {
          // Phone number
          parts.push({ type: "phone", value: match[3] });
        }

        lastIndex = match.index + match[0].length;
      }

      // Add remaining text
      if (lastIndex < line.length) {
        parts.push({ type: "text", value: line.slice(lastIndex) });
      }

      // If line is empty, just render a line break
      if (parts.length === 0) {
        parts.push({ type: "text", value: "" });
      }

      return (
        <React.Fragment key={lineIndex}>
          {lineIndex > 0 && <br />}
          {parts.map((part, partIndex) => {
            if (part.type === "url") {
              return (
                <Link
                  key={partIndex}
                  href={part.value}
                  isExternal
                  color={linkColor}
                  textDecoration="underline"
                  wordBreak="break-all"
                >
                  {part.value}
                </Link>
              );
            }
            if (part.type === "email") {
              return (
                <Link
                  key={partIndex}
                  href={`mailto:${part.value}`}
                  color={linkColor}
                  textDecoration="underline"
                >
                  {part.value}
                </Link>
              );
            }
            if (part.type === "phone") {
              const digits = part.value.replace(/\D/g, "");
              return (
                <Link
                  key={partIndex}
                  href={`tel:+1${digits.slice(-10)}`}
                  color={linkColor}
                  textDecoration="underline"
                >
                  {part.value}
                </Link>
              );
            }
            return <span key={partIndex}>{part.value}</span>;
          })}
        </React.Fragment>
      );
    });
  };

  // Keep backward-compatible alias
  const renderMessageWithLinks = (text) => renderFormattedText(text);

  // Toggle expand/collapse for a form group
  const toggleFormExpand = (groupId) => {
    setExpandedForms((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  // Check if message starts with "Part 1:"
  const isPart1Message = (message) => {
    return message?.trim().startsWith("Part 1:");
  };

  // Check if message starts with "Part 2"
  const isPart2Message = (message) => {
    return message?.trim().startsWith("Part 2");
  };

  // Appointment Section Header Component
  const AppointmentSectionHeader = ({
    appointmentDate,
    messageCount,
    isExpanded,
    onToggle,
    colorScheme = "blue",
  }) => (
    <Box
      bg={`${colorScheme}.50`}
      borderWidth="1px"
      borderColor={`${colorScheme}.200`}
      borderRadius="md"
      cursor="pointer"
      onClick={onToggle}
      _hover={{ bg: `${colorScheme}.100` }}
      transition="background 0.2s"
    >
      <HStack justify="space-between" p={3}>
        <HStack spacing={3}>
          <CalendarIcon color={`${colorScheme}.500`} />
          <Text fontWeight="semibold" color={`${colorScheme}.700`}>
            {formatAppointmentDate(appointmentDate)}
          </Text>
          <Badge colorScheme={colorScheme} variant="solid">
            {messageCount} {messageCount === 1 ? "item" : "items"}
          </Badge>
        </HStack>
        <IconButton
          icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          aria-label={isExpanded ? "Collapse" : "Expand"}
          variant="ghost"
          size="sm"
          colorScheme={colorScheme}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        />
      </HStack>
    </Box>
  );

  // Uncategorized Section Header Component
  const UncategorizedSectionHeader = ({
    messageCount,
    isExpanded,
    onToggle,
  }) => (
    <Box
      bg="gray.100"
      borderWidth="1px"
      borderColor="gray.300"
      borderRadius="md"
      cursor="pointer"
      onClick={onToggle}
      _hover={{ bg: "gray.200" }}
      transition="background 0.2s"
    >
      <HStack justify="space-between" p={3}>
        <HStack spacing={3}>
          <WarningIcon color="gray.500" />
          <Text fontWeight="semibold" color="gray.600">
            Other Messages (No Matching Appointment)
          </Text>
          <Badge colorScheme="gray" variant="solid">
            {messageCount} {messageCount === 1 ? "item" : "items"}
          </Badge>
        </HStack>
        <IconButton
          icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
          aria-label={isExpanded ? "Collapse" : "Expand"}
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        />
      </HStack>
    </Box>
  );

  // Render a single form group item
  const renderFormGroupItem = (group, index) => {
    const groupId = `form-group-${group.part1?.id || index}-${index}`;
    const isExpanded = expandedForms[groupId];
    const part1Urls = extractUrls(group.part1?.message);
    const part2Urls = extractUrls(group.part2?.message);
    const allUrls = [...part1Urls, ...part2Urls];

    return (
      <Box
        key={groupId}
        p={4}
        bg="white"
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.200"
      >
        {/* Header with date and buttons */}
        <HStack justify="space-between" mb={3}>
          <HStack>
            <Badge colorScheme="purple">
              {group.type === "form_group" ? "Form Messages" : "Form Link"}
            </Badge>
            <Text fontSize="xs" color="gray.500">
              {formatDate(group.created_at)}
            </Text>
          </HStack>
          <HStack spacing={2}>
            {allUrls.length > 0 && (
              <Link href={allUrls[0]} isExternal>
                <Button
                  size="xs"
                  leftIcon={<ViewIcon />}
                  colorScheme="green"
                  variant="outline"
                >
                  View Form
                </Button>
              </Link>
            )}
            <Tooltip
              label={
                group.part2 ? "Resend Part 1 & Part 2" : "Resend form message"
              }
              placement="top"
            >
              <Button
                size="xs"
                leftIcon={<RepeatIcon />}
                colorScheme="blue"
                isLoading={sendingMessage}
                onClick={() => resendFormGroup(group.part1, group.part2)}
              >
                Resend {group.part2 ? "Both" : ""}
              </Button>
            </Tooltip>
            <IconButton
              size="xs"
              icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
              aria-label={isExpanded ? "Collapse" : "Expand"}
              variant="ghost"
              onClick={() => toggleFormExpand(groupId)}
            />
          </HStack>
        </HStack>

        {/* Part 1 Message */}
        {group.part1 && (
          <Box
            p={3}
            bg="gray.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor={isMessageFailed(group.part1) ? "red.300" : "blue.200"}
            mb={group.part2 ? 2 : 0}
          >
            <HStack mb={1}>
              {isPart1Message(group.part1.message) && (
                <Badge colorScheme="blue" size="sm">
                  Part 1
                </Badge>
              )}
              {group.part1.status && (
                <Badge
                  size="sm"
                  colorScheme={
                    group.part1.status === "delivered"
                      ? "green"
                      : group.part1.status === "failed" ||
                          group.part1.status === "undelivered"
                        ? "red"
                        : "gray"
                  }
                >
                  {group.part1.status}
                </Badge>
              )}
            </HStack>
            {isExpanded ? (
              <Text fontSize="sm" whiteSpace="pre-wrap">
                {renderMessageWithLinks(group.part1.message)}
              </Text>
            ) : (
              <Text fontSize="sm" whiteSpace="pre-wrap" noOfLines={3}>
                {group.part1.message}
              </Text>
            )}
            {renderErrorPreview(group.part1)}
          </Box>
        )}

        {/* Part 2 Message */}
        {group.part2 && (
          <Box
            p={3}
            bg="gray.50"
            borderRadius="md"
            borderWidth="1px"
            borderColor={isMessageFailed(group.part2) ? "red.300" : "green.200"}
          >
            <HStack mb={1}>
              {isPart2Message(group.part2.message) && (
                <Badge colorScheme="green" size="sm">
                  Part 2
                </Badge>
              )}
              {group.part2.status && (
                <Badge
                  size="sm"
                  colorScheme={
                    group.part2.status === "delivered"
                      ? "green"
                      : group.part2.status === "failed" ||
                          group.part2.status === "undelivered"
                        ? "red"
                        : "gray"
                  }
                >
                  {group.part2.status}
                </Badge>
              )}
            </HStack>
            {isExpanded ? (
              <Text fontSize="sm" whiteSpace="pre-wrap">
                {renderMessageWithLinks(group.part2.message)}
              </Text>
            ) : (
              <Text fontSize="sm" whiteSpace="pre-wrap" noOfLines={3}>
                {group.part2.message}
              </Text>
            )}
            {renderErrorPreview(group.part2)}
          </Box>
        )}

        {/* Expand/Collapse hint */}
        {!isExpanded && (
          <Text
            fontSize="xs"
            color="blue.500"
            mt={2}
            cursor="pointer"
            onClick={() => toggleFormExpand(groupId)}
            _hover={{ textDecoration: "underline" }}
          >
            Click to expand full message...
          </Text>
        )}
      </Box>
    );
  };

  // Render Forms Tab content
  const renderFormsTab = () => {
    if (loadingForms) {
      return (
        <Flex justify="center" align="center" py={8}>
          <Spinner size="lg" />
          <Text ml={3}>Loading form messages...</Text>
        </Flex>
      );
    }

    const { categorized, uncategorized } = categorizedForms;
    const appointmentDates = Object.keys(categorized).sort(
      (a, b) => new Date(b) - new Date(a),
    );
    const hasAnyCategorized = appointmentDates.length > 0;
    const hasUncategorized = uncategorized.length > 0;

    return (
      <VStack spacing={4} align="stretch">
        {/* Refresh Button for Forms */}
        <HStack justify="space-between">
          <HStack>
            <Text fontWeight="bold" fontSize="lg">
              Form Messages Sent
            </Text>
            <Badge colorScheme="blue">{formsSent.length}</Badge>
          </HStack>
          <HStack spacing={2}>
            <Text fontSize="xs" color="gray.400">
              Updated{" "}
              {lastRefreshedForms.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
            <Button
              size="xs"
              leftIcon={<RepeatIcon />}
              onClick={fetchFormsData}
              isLoading={loadingForms}
            >
              Refresh
            </Button>
          </HStack>
        </HStack>

        {formsSent.length === 0 ? (
          <Text color="gray.500" fontSize="sm">
            No form messages sent to this patient
          </Text>
        ) : (
          <VStack spacing={3} align="stretch">
            {/* Categorized by appointment date */}
            {appointmentDates.map((appointmentDate, dateIndex) => {
              const section = categorized[appointmentDate];
              const isExpanded =
                expandedAppointments.forms?.[appointmentDate] ??
                dateIndex === 0;
              const formsInSection = section.messages || [];

              return (
                <Box key={`forms-appt-${appointmentDate}`}>
                  <AppointmentSectionHeader
                    appointmentDate={appointmentDate}
                    messageCount={formsInSection.length}
                    isExpanded={isExpanded}
                    onToggle={() =>
                      toggleAppointmentSection("forms", appointmentDate)
                    }
                    colorScheme="purple"
                  />
                  <Collapse in={isExpanded} animateOpacity>
                    <VStack
                      spacing={3}
                      align="stretch"
                      mt={2}
                      pl={2}
                      borderLeftWidth="2px"
                      borderLeftColor="purple.200"
                    >
                      {formsInSection.map((group, index) =>
                        renderFormGroupItem(group, index),
                      )}
                    </VStack>
                  </Collapse>
                </Box>
              );
            })}

            {/* Uncategorized forms */}
            {hasUncategorized && (
              <Box>
                <UncategorizedSectionHeader
                  messageCount={uncategorized.length}
                  isExpanded={
                    expandedAppointments.forms?.["_uncategorized"] ?? false
                  }
                  onToggle={() =>
                    toggleAppointmentSection("forms", "_uncategorized")
                  }
                />
                <Collapse
                  in={expandedAppointments.forms?.["_uncategorized"] ?? false}
                  animateOpacity
                >
                  <VStack
                    spacing={3}
                    align="stretch"
                    mt={2}
                    pl={2}
                    borderLeftWidth="2px"
                    borderLeftColor="gray.300"
                  >
                    {uncategorized.map((group, index) =>
                      renderFormGroupItem(group, index),
                    )}
                  </VStack>
                </Collapse>
              </Box>
            )}

            {/* If no categorization happened, show all forms flat */}
            {!hasAnyCategorized &&
              !hasUncategorized &&
              formsSent.map((group, index) =>
                renderFormGroupItem(group, index),
              )}
          </VStack>
        )}
      </VStack>
    );
  };

  // Check if a message is from the patient
  const isFromPatientMessage = (msg, normalizedPhone) => {
    return isPatientAuthoredMessage(msg, isPatientNumber);
  };

  // Check if a message is a new (unread) message based on unreadCount
  // Messages are sorted oldest first, so we look at the last N messages from patient
  const isNewMessage = (msg, messageList, normalizedPhone, unreadCnt) => {
    const isNew = isNewestUnreadMessage(
      msg,
      messageList,
      isPatientNumber,
      unreadCnt,
    );
    console.log("[isNewMessage] Check:", {
      msgId: msg.id,
      msgIndex: messageList.indexOf(msg),
      unreadCnt,
      isNew,
      normalizedPhone,
      msgFrom: msg.from,
    });
    // The last N patient messages are new
    return isNew;
  };

  // Render a single message bubble
  const renderMessageBubble = (
    msg,
    index,
    normalizedPhone,
    isUnread = false,
  ) => {
    if (isPatientMessageSystemEvent(msg)) {
      return (
        <PatientMessageSystemEvent
          key={`msg-${msg.id}-${index}`}
          message={msg.message}
          formattedTimestamp={formatDate(msg.created_at)}
        />
      );
    }

    const isFromPatient = isPatientNumber(msg.from);
    const isFailed = isMessageFailed(msg);

    const statusColor =
      msg.status === "delivered"
        ? "green"
        : msg.status === "failed" || msg.status === "undelivered"
          ? "red"
          : "gray";

    return (
      <Box
        key={`msg-${msg.id}-${index}`}
        alignSelf={isFromPatient ? "flex-start" : "flex-end"}
        maxW="70%"
        id={isUnread ? `unread-msg-${msg.id}` : undefined}
      >
        {/* Timestamp + status + resend above the bubble */}
        <HStack
          justify={isFromPatient ? "flex-start" : "flex-end"}
          mb={1}
          spacing={2}
        >
          <Badge
            size="sm"
            colorScheme={isFromPatient ? "gray" : statusColor}
            variant="subtle"
            fontWeight="bold"
            fontSize="xs"
          >
            {formatDate(msg.created_at)}
          </Badge>
          {!isFromPatient && msg.status && (
            <Badge size="xs" colorScheme={statusColor}>
              {msg.status}
            </Badge>
          )}
          {!isFromPatient && msg.message && (
            <Tooltip
              label={isFailed ? "Resend failed message" : "Resend message"}
              placement="top"
            >
              <IconButton
                icon={isFailed ? <EditIcon /> : <RepeatIcon />}
                aria-label={
                  isFailed ? "Resend failed message" : "Resend message"
                }
                size="xs"
                variant="ghost"
                colorScheme={isFailed ? "red" : "blue"}
                isLoading={sendingMessage}
                onClick={() => {
                  if (isFailed) {
                    // Open resend modal for failed messages
                    setResendMessageData(msg);
                    setResendSplitMessage(false); // Reset split option
                    onResendModalOpen();
                  } else {
                    resendMessage(msg.message);
                  }
                }}
              />
            </Tooltip>
          )}
        </HStack>
        <Box
          bg={
            isFromPatient
              ? isUnread
                ? "green.100"
                : "white"
              : isFailed
                ? "red.100"
                : "blue.500"
          }
          color={isFromPatient ? "black" : isFailed ? "red.800" : "white"}
          p={3}
          borderRadius="lg"
          boxShadow="sm"
          borderWidth={isFromPatient || isFailed ? "1px" : "0"}
          borderColor={
            isFromPatient && isUnread
              ? "green.200"
              : isFailed
                ? "red.300"
                : "transparent"
          }
        >
          <Text fontSize="sm">
            {renderFormattedText(
              msg.message,
              isFromPatient
                ? "blue.500"
                : isFailed
                  ? "red.300"
                  : "whiteAlpha.900",
            )}
          </Text>
        </Box>
        {/* Error preview for failed messages */}
        {!isFromPatient && isFailed && (
          <Box alignSelf="flex-end" maxW="100%" mt={1}>
            {renderErrorPreview(msg)}
          </Box>
        )}
      </Box>
    );
  };

  // Render Messages Tab content
  const renderMessagesTab = () => {
    const patientPhone = getPatientPhone();
    const normalizedPhone = getNormalizedPhone();

    if (loadingMessages) {
      return (
        <Flex justify="center" align="center" py={8}>
          <Spinner size="lg" />
          <Text ml={3}>Loading messages...</Text>
        </Flex>
      );
    }

    if (!patientPhone) {
      return (
        <Alert status="warning">
          <AlertIcon />
          <AlertTitle>No Phone Number</AlertTitle>
          <AlertDescription>
            This patient does not have a phone number on file. Messages cannot
            be sent or received.
          </AlertDescription>
        </Alert>
      );
    }

    const { categorized, uncategorized } = categorizedMessages;
    // Sort ascending (oldest first, newest at bottom) for chat-like display
    const appointmentDates = Object.keys(categorized).sort(
      (a, b) => new Date(a) - new Date(b),
    );
    const hasAnyCategorized = appointmentDates.length > 0;
    const hasUncategorized = uncategorized.length > 0;

    return (
      <VStack spacing={4} align="stretch" h="100%">
        {/* Header with Refresh Button */}
        <HStack justify="space-between">
          <HStack>
            <Text fontWeight="bold" fontSize="lg">
              Messages
            </Text>
            <Badge colorScheme="green">{messages.length}</Badge>
          </HStack>
          <HStack spacing={2}>
            <Text fontSize="xs" color="gray.400">
              Updated{" "}
              {lastRefreshedMessages.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Text>
            <Button
              size="xs"
              leftIcon={<RepeatIcon />}
              onClick={fetchMessages}
              isLoading={loadingMessages}
            >
              Refresh
            </Button>
          </HStack>
        </HStack>

        {/* Messages List grouped by appointment */}
        <Box
          ref={messagesContainerRef}
          onScroll={handleScroll}
          overflowY="auto"
          maxH="400px"
          position="relative"
        >
          {messages.length === 0 ? (
            <Box p={3} bg="gray.50" borderRadius="md">
              <Text color="gray.500" textAlign="center" py={8}>
                No messages yet. Send a message to start a conversation.
              </Text>
            </Box>
          ) : (
            <VStack spacing={3} align="stretch">
              {/* Categorized by appointment date (oldest first, newest at bottom) */}
              {appointmentDates.map((appointmentDate, dateIndex) => {
                const section = categorized[appointmentDate];
                // Expand the last (newest) section by default
                const isExpanded =
                  expandedAppointments.messages?.[appointmentDate] ??
                  dateIndex === appointmentDates.length - 1;
                const messagesInSection = section.messages || [];

                return (
                  <Box key={`msgs-appt-${appointmentDate}`}>
                    <AppointmentSectionHeader
                      appointmentDate={appointmentDate}
                      messageCount={messagesInSection.length}
                      isExpanded={isExpanded}
                      onToggle={() =>
                        toggleAppointmentSection("messages", appointmentDate)
                      }
                      colorScheme="green"
                    />
                    <Collapse in={isExpanded} animateOpacity>
                      <Box
                        mt={2}
                        p={3}
                        bg="gray.50"
                        borderRadius="md"
                        borderLeftWidth="2px"
                        borderLeftColor="green.200"
                      >
                        <VStack spacing={2} align="stretch">
                          {messagesInSection.map((msg, index) => {
                            // Calculate if this message is one of the newest (unread) messages
                            // Messages are sorted oldest first, so we check from the end
                            const isUnread =
                              isFromPatientMessage(msg, normalizedPhone) &&
                              isNewMessage(
                                msg,
                                messagesInSection,
                                normalizedPhone,
                                unreadCount,
                              );
                            return renderMessageBubble(
                              msg,
                              index,
                              normalizedPhone,
                              isUnread,
                            );
                          })}
                        </VStack>
                        <div id={`msgs-section-end-${appointmentDate}`} />
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}

              {/* Uncategorized messages */}
              {hasUncategorized && (
                <Box>
                  <UncategorizedSectionHeader
                    messageCount={uncategorized.length}
                    isExpanded={
                      expandedAppointments.messages?.["_uncategorized"] ?? false
                    }
                    onToggle={() =>
                      toggleAppointmentSection("messages", "_uncategorized")
                    }
                  />
                  <Collapse
                    in={
                      expandedAppointments.messages?.["_uncategorized"] ?? false
                    }
                    animateOpacity
                  >
                    <Box
                      mt={2}
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      borderLeftWidth="2px"
                      borderLeftColor="gray.300"
                    >
                      <VStack spacing={2} align="stretch">
                        {uncategorized.map((msg, index) => {
                          const isUnread =
                            isFromPatientMessage(msg, normalizedPhone) &&
                            isNewMessage(
                              msg,
                              uncategorized,
                              normalizedPhone,
                              unreadCount,
                            );
                          return renderMessageBubble(
                            msg,
                            index,
                            normalizedPhone,
                            isUnread,
                          );
                        })}
                      </VStack>
                    </Box>
                  </Collapse>
                </Box>
              )}

              {/* If no categorization happened, show all messages flat */}
              {!hasAnyCategorized && !hasUncategorized && (
                <Box p={3} bg="gray.50" borderRadius="md">
                  <VStack spacing={2} align="stretch">
                    {messages.map((msg, index) => {
                      const isUnread =
                        isFromPatientMessage(msg, normalizedPhone) &&
                        isNewMessage(
                          msg,
                          messages,
                          normalizedPhone,
                          unreadCount,
                        );
                      return renderMessageBubble(
                        msg,
                        index,
                        normalizedPhone,
                        isUnread,
                      );
                    })}
                  </VStack>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </VStack>
          )}
        </Box>

        {/* Scroll to bottom button */}
        {messages.length > 0 && (
          <Flex
            ref={scrollBtnRef}
            justify="flex-end"
            mt={-10}
            mr={2}
            position="relative"
            zIndex={2}
            transition="opacity 0.15s"
          >
            <IconButton
              icon={<ChevronDownIcon boxSize={6} />}
              aria-label="Scroll to bottom"
              size="sm"
              borderRadius="full"
              bg="gray.700"
              color="white"
              _hover={{ bg: "gray.900" }}
              boxShadow="lg"
              onClick={scrollToBottom}
            />
          </Flex>
        )}

        {/* Message Input - using ref for performance */}
        <HStack>
          <Input
            ref={messageInputRef}
            placeholder="Type a message..."
            defaultValue=""
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />
          <Button
            colorScheme="blue"
            onClick={sendMessage}
            isLoading={sendingMessage}
          >
            Send
          </Button>
        </HStack>
      </VStack>
    );
  };

  // Render a single AI message item
  const renderAiMessageItem = (msg, index, normalizedPhone) => {
    const isFromPatient = isPatientNumber(msg.from);
    const isFailed = isMessageFailed(msg);

    return (
      <Box
        key={`ai-${msg.id}-${index}`}
        bg={isFailed && !isFromPatient ? "red.50" : "white"}
        p={3}
        borderRadius="md"
        boxShadow="sm"
        borderLeftWidth="4px"
        borderLeftColor={
          isFailed && !isFromPatient
            ? "red.400"
            : isFromPatient
              ? "blue.400"
              : "green.400"
        }
        borderWidth={isFailed && !isFromPatient ? "1px" : "0"}
        borderColor={isFailed && !isFromPatient ? "red.200" : "transparent"}
      >
        <HStack justify="space-between" mb={2}>
          <HStack>
            <Badge colorScheme={isFromPatient ? "blue" : "green"}>
              {isFromPatient ? "Patient" : "AI Response"}
            </Badge>
            {!isFromPatient && msg.status && (
              <Badge
                size="sm"
                colorScheme={
                  msg.status === "delivered"
                    ? "green"
                    : msg.status === "failed" || msg.status === "undelivered"
                      ? "red"
                      : "gray"
                }
              >
                {msg.status}
              </Badge>
            )}
          </HStack>
          <Badge
            size="sm"
            colorScheme={isFromPatient ? "blue" : isFailed ? "red" : "green"}
            variant="subtle"
            fontWeight="bold"
            fontSize="xs"
          >
            {formatDate(msg.created_at)}
          </Badge>
        </HStack>
        <Text fontSize="sm">{renderFormattedText(msg.message)}</Text>
        {msg.ai_flagged && (
          <Badge mt={2} colorScheme="red">
            <WarningIcon mr={1} /> Flagged: {msg.ai_flag_reason}
          </Badge>
        )}
        {/* Error preview for failed AI responses */}
        {!isFromPatient && isFailed && renderErrorPreview(msg)}
      </Box>
    );
  };

  // Render AI Chat Tab content
  const renderAiChatTab = () => {
    const patientPhone = getPatientPhone();
    const normalizedPhone = getNormalizedPhone();

    if (loadingAiMessages) {
      return (
        <Flex justify="center" align="center" py={8}>
          <Spinner size="lg" />
          <Text ml={3}>Loading AI chat messages...</Text>
        </Flex>
      );
    }

    if (!patientPhone) {
      return (
        <Alert status="warning">
          <AlertIcon />
          <AlertTitle>No Phone Number</AlertTitle>
          <AlertDescription>
            This patient does not have a phone number on file. AI chat is not
            available.
          </AlertDescription>
        </Alert>
      );
    }

    const { categorized, uncategorized } = categorizedAiMessages;
    // Sort ascending (oldest first, newest at bottom) for chat-like display
    const appointmentDates = Object.keys(categorized).sort(
      (a, b) => new Date(a) - new Date(b),
    );
    const hasAnyCategorized = appointmentDates.length > 0;
    const hasUncategorized = uncategorized.length > 0;

    return (
      <VStack spacing={4} align="stretch">
        {/* Flagged Messages Alert */}
        {flaggedMessages.length > 0 && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>
                Flagged Questions ({flaggedMessages.length})
              </AlertTitle>
              <AlertDescription>
                The AI could not answer these questions and flagged them for
                your review.
              </AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Flagged Messages Section */}
        {flaggedMessages.length > 0 && (
          <Box
            bg="yellow.50"
            p={4}
            borderRadius="md"
            borderWidth="1px"
            borderColor="yellow.200"
          >
            <Text fontWeight="bold" mb={3} color="yellow.800">
              <WarningIcon mr={2} />
              Questions Requiring Review
            </Text>
            <VStack spacing={3} align="stretch">
              {flaggedMessages.map((msg, index) => (
                <Box
                  key={`flagged-${msg.id}-${index}`}
                  bg="white"
                  p={3}
                  borderRadius="md"
                  boxShadow="sm"
                >
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="xs" color="gray.500">
                      {formatDate(msg.created_at)}
                    </Text>
                    <Badge colorScheme="red">
                      {msg.ai_flag_reason || "Needs Review"}
                    </Badge>
                  </HStack>
                  <Text fontWeight="medium" mb={2}>
                    Patient asked:
                  </Text>
                  <Text bg="gray.100" p={2} borderRadius="md" mb={2}>
                    {renderFormattedText(msg.message)}
                  </Text>
                  {msg.ai_response && (
                    <>
                      <Text fontWeight="medium" mb={2}>
                        AI Response:
                      </Text>
                      <Text
                        bg="blue.50"
                        p={2}
                        borderRadius="md"
                        mb={2}
                        fontSize="sm"
                      >
                        {renderFormattedText(msg.ai_response)}
                      </Text>
                    </>
                  )}
                  <Button
                    size="sm"
                    colorScheme="green"
                    leftIcon={<CheckIcon />}
                    onClick={() => markAsReviewed(msg.id)}
                  >
                    Mark as Reviewed
                  </Button>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        <Divider />

        {/* All AI Chat Messages */}
        <Box>
          <HStack mb={3} justify="space-between">
            <HStack>
              <ChatIcon />
              <Text fontWeight="bold" fontSize="lg">
                AI Chat History
              </Text>
              <Badge colorScheme="purple">{aiMessages.length}</Badge>
            </HStack>
            <HStack spacing={2}>
              <Text fontSize="xs" color="gray.400">
                Updated{" "}
                {lastRefreshedAi.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Text>
              <Button
                size="xs"
                leftIcon={<RepeatIcon />}
                onClick={fetchAiMessages}
              >
                Refresh
              </Button>
            </HStack>
          </HStack>

          <Box
            ref={aiMessagesContainerRef}
            onScroll={handleAiScroll}
            maxH="400px"
            overflowY="auto"
            position="relative"
          >
            {aiMessages.length === 0 ? (
              <Box p={3} bg="gray.50" borderRadius="md">
                <Text color="gray.500" textAlign="center" py={8}>
                  No AI chat messages yet. AI chat is triggered when patients
                  text keywords like "AI chat" or "AI assistant".
                </Text>
              </Box>
            ) : (
              <VStack spacing={3} align="stretch">
                {/* Categorized by appointment date (oldest first, newest at bottom) */}
                {appointmentDates.map((appointmentDate, dateIndex) => {
                  const section = categorized[appointmentDate];
                  // Expand the last (newest) section by default
                  const isExpanded =
                    expandedAppointments.ai?.[appointmentDate] ??
                    dateIndex === appointmentDates.length - 1;
                  const messagesInSection = section.messages || [];

                  return (
                    <Box key={`ai-appt-${appointmentDate}`}>
                      <AppointmentSectionHeader
                        appointmentDate={appointmentDate}
                        messageCount={messagesInSection.length}
                        isExpanded={isExpanded}
                        onToggle={() =>
                          toggleAppointmentSection("ai", appointmentDate)
                        }
                        colorScheme="teal"
                      />
                      <Collapse in={isExpanded} animateOpacity>
                        <Box
                          mt={2}
                          p={3}
                          bg="gray.50"
                          borderRadius="md"
                          borderLeftWidth="2px"
                          borderLeftColor="teal.200"
                        >
                          <VStack spacing={3} align="stretch">
                            {messagesInSection.map((msg, index) =>
                              renderAiMessageItem(msg, index, normalizedPhone),
                            )}
                          </VStack>
                          <div id={`ai-section-end-${appointmentDate}`} />
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}

                {/* Uncategorized AI messages */}
                {hasUncategorized && (
                  <Box>
                    <UncategorizedSectionHeader
                      messageCount={uncategorized.length}
                      isExpanded={
                        expandedAppointments.ai?.["_uncategorized"] ?? false
                      }
                      onToggle={() =>
                        toggleAppointmentSection("ai", "_uncategorized")
                      }
                    />
                    <Collapse
                      in={expandedAppointments.ai?.["_uncategorized"] ?? false}
                      animateOpacity
                    >
                      <Box
                        mt={2}
                        p={3}
                        bg="gray.50"
                        borderRadius="md"
                        borderLeftWidth="2px"
                        borderLeftColor="gray.300"
                      >
                        <VStack spacing={3} align="stretch">
                          {uncategorized.map((msg, index) =>
                            renderAiMessageItem(msg, index, normalizedPhone),
                          )}
                        </VStack>
                      </Box>
                    </Collapse>
                  </Box>
                )}

                {/* If no categorization happened, show all AI messages flat */}
                {!hasAnyCategorized && !hasUncategorized && (
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <VStack spacing={3} align="stretch">
                      {aiMessages.map((msg, index) =>
                        renderAiMessageItem(msg, index, normalizedPhone),
                      )}
                    </VStack>
                  </Box>
                )}

                <div ref={aiMessagesEndRef} />
              </VStack>
            )}
          </Box>

          {/* Scroll to bottom button */}
          {aiMessages.length > 0 && (
            <Flex
              ref={aiScrollBtnRef}
              justify="flex-end"
              mt={-10}
              mr={2}
              position="relative"
              zIndex={2}
              transition="opacity 0.15s"
            >
              <IconButton
                icon={<ChevronDownIcon boxSize={6} />}
                aria-label="Scroll to bottom"
                size="sm"
                borderRadius="full"
                bg="gray.700"
                color="white"
                _hover={{ bg: "gray.900" }}
                boxShadow="lg"
                onClick={scrollToAiBottom}
              />
            </Flex>
          )}
        </Box>

        {/* AI Chat Info */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">How AI Chat Works</AlertTitle>
            <AlertDescription fontSize="xs">
              When patients text keywords like "AI chat", "AI assistant", or
              "help me with", the AI will automatically respond to simple
              questions about appointments, dosages, and scheduling. Complex
              medical questions are flagged for your review.
            </AlertDescription>
          </Box>
        </Alert>
      </VStack>
    );
  };

  // Handle closing the modal - call onMessagesViewed then onClose
  const handleClose = useCallback(() => {
    if (onMessagesViewed) {
      onMessagesViewed();
    }
    onClose();
  }, [onMessagesViewed, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          <HStack>
            <Text>Patient Messages</Text>
            <Badge colorScheme="blue" ml={2}>
              {getPatientName()}
            </Badge>
            {getPatientPhones().length > 1 ? (
              <PatientPhones row={getPatientRow()} compact inline fontSize="sm" fontWeight="normal" color="gray.600" />
            ) : (
              getPatientPhone() && (
                <>
                  <Text fontSize="sm" fontWeight="normal" color="gray.600">
                    {formatPhone(getPatientPhone())}
                  </Text>
                  <PhoneStatusBadge phone={getPatientPhone()} />
                </>
              )
            )}
          </HStack>
          {smsTarget && getPatientPhones().length > 1 && (
            <Text
              fontSize="xs"
              fontWeight="normal"
              mt={1}
              color={smsTarget.blocked ? "red.600" : "blue.600"}
              data-testid="sms-target-note"
            >
              {smsTarget.blocked
                ? smsTarget.reason === "opt_out"
                  ? "This patient opted out — no texts will be sent."
                  : "Both numbers are known bad — texts cannot be sent."
                : smsTarget.slot === "home"
                  ? `Texts go to the home phone ${formatPhone(smsTarget.phone)} (the cell is a bad number).`
                  : "Texts go to the cell phone."}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Tabs
            index={activeTabIndex}
            onChange={setActiveTabIndex}
            variant="enclosed"
            colorScheme="blue"
          >
            <TabList>
              <Tab>
                Forms
                {formsSent.length > 0 && (
                  <Badge ml={2} colorScheme="blue">
                    {formsSent.length}
                  </Badge>
                )}
              </Tab>
              <Tab>
                Messages
                {messages.length > 0 && (
                  <Badge ml={2} colorScheme="green">
                    {messages.length}
                  </Badge>
                )}
              </Tab>
              <Tab>
                AI Chat
                {flaggedMessages.length > 0 && (
                  <Badge ml={2} colorScheme="red">
                    {flaggedMessages.length} flagged
                  </Badge>
                )}
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel>{renderFormsTab()}</TabPanel>
              <TabPanel>{renderMessagesTab()}</TabPanel>
              <TabPanel>{renderAiChatTab()}</TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>

      {/* Message Details Modal */}
      <Modal
        isOpen={isMessageDetailsOpen}
        onClose={onMessageDetailsClose}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex justify="space-between" align="center">
              <Text>Message Details</Text>
              <Button
                size="sm"
                leftIcon={<CopyIcon />}
                colorScheme="blue"
                variant="outline"
                onClick={handleCopyMessage}
              >
                {copied ? "Copied!" : "Copy"}
              </Button>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingMessage && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Time
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    {formatDate(viewingMessage.created_at)}
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Direction
                  </Text>
                  <Badge
                    colorScheme={
                      isPatientNumber(viewingMessage.from)
                        ? "green"
                        : "blue"
                    }
                  >
                    {isPatientNumber(viewingMessage.from)
                      ? "📥 Received from patient"
                      : "📤 Sent to patient"}
                  </Badge>
                </Box>

                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Phone Numbers
                  </Text>
                  {/* Status only next to the patient's number, not our sender */}
                  <HStack spacing={2}>
                    <Text fontSize="sm" fontFamily="mono" color="gray.600">
                      From: {viewingMessage.from || "N/A"}
                    </Text>
                    {isPatientNumber(viewingMessage.from) && (
                      <PhoneStatusBadge phone={viewingMessage.from} />
                    )}
                  </HStack>
                  <HStack spacing={2}>
                    <Text fontSize="sm" fontFamily="mono" color="gray.600">
                      To: {viewingMessage.to || "N/A"}
                    </Text>
                    {isPatientNumber(viewingMessage.to) && (
                      <PhoneStatusBadge phone={viewingMessage.to} />
                    )}
                  </HStack>
                </Box>

                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Status
                  </Text>
                  <Badge
                    colorScheme={
                      viewingMessage.status === "delivered" ||
                      viewingMessage.status === "received"
                        ? "green"
                        : viewingMessage.status === "failed" ||
                            viewingMessage.status === "undelivered"
                          ? "red"
                          : viewingMessage.status === "sent"
                            ? "blue"
                            : "gray"
                    }
                    variant="subtle"
                  >
                    {viewingMessage.status || "Unknown"}
                  </Badge>
                </Box>

                <Divider />

                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Message Content
                  </Text>
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    borderWidth={1}
                    borderColor="gray.200"
                  >
                    <Text
                      fontSize="sm"
                      whiteSpace="pre-wrap"
                      wordBreak="break-word"
                      color="gray.800"
                    >
                      {viewingMessage.message
                        ? renderFormattedText(viewingMessage.message)
                        : "(no content)"}
                    </Text>
                  </Box>
                </Box>

                {(viewingMessage.error_message ||
                  viewingMessage.error_code) && (
                  <Box>
                    <Text fontWeight="semibold" mb={2} color="red.600">
                      Error Details
                    </Text>
                    {viewingMessage.error_code && (
                      <Text fontSize="sm" color="red.500" mb={1}>
                        Error Code: {viewingMessage.error_code}
                      </Text>
                    )}
                    <Box
                      p={4}
                      bg="red.50"
                      borderRadius="md"
                      borderWidth={1}
                      borderColor="red.200"
                    >
                      <Text
                        fontSize="sm"
                        whiteSpace="pre-wrap"
                        wordBreak="break-word"
                        color="red.800"
                      >
                        {viewingMessage.error_message ||
                          "Message delivery failed. The message could not be delivered to the recipient."}
                      </Text>
                    </Box>
                  </Box>
                )}

                {viewingMessage.twilio_sid && (
                  <Box>
                    <Text fontWeight="semibold" mb={2}>
                      Twilio SID
                    </Text>
                    <Text fontSize="xs" fontFamily="mono" color="gray.500">
                      {viewingMessage.twilio_sid}
                    </Text>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {isMessageFailed(viewingMessage) && (
              <Button
                colorScheme="red"
                variant="outline"
                mr={3}
                leftIcon={<EditIcon />}
                onClick={() => {
                  onMessageDetailsClose();
                  if (viewingMessage) {
                    setResendMessageData(viewingMessage);
                    setResendSplitMessage(false);
                    onResendModalOpen();
                  }
                }}
              >
                Resend Failed Message
              </Button>
            )}
            <Button colorScheme="blue" onClick={onMessageDetailsClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Resend Failed Message Modal */}
      <Modal isOpen={isResendModalOpen} onClose={onResendModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Resend Failed Message</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {resendMessageData && (
              <VStack spacing={4} align="stretch">
                {/* Error Details Section */}
                <Box>
                  <Text fontWeight="semibold" mb={2} color="red.600">
                    Error Details
                  </Text>
                  {resendMessageData.error_code && (
                    <Text fontSize="sm" color="red.500" mb={2}>
                      <Text as="span" fontWeight="bold">
                        Error Code:{" "}
                      </Text>
                      {resendMessageData.error_code}
                    </Text>
                  )}
                  <Box
                    p={3}
                    bg="red.50"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="red.200"
                  >
                    <Text fontSize="sm" whiteSpace="pre-wrap" color="red.800">
                      {resendMessageData.error_message ||
                        "Message delivery failed. The message could not be delivered to the recipient."}
                    </Text>
                  </Box>
                </Box>

                {/* Message Preview */}
                <Box>
                  <Text fontWeight="semibold" mb={2}>
                    Message Content
                  </Text>
                  <Box
                    p={3}
                    bg="gray.50"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="gray.200"
                  >
                    <Text fontSize="sm" whiteSpace="pre-wrap">
                      {resendMessageData.message}
                    </Text>
                  </Box>
                </Box>

                {/* Split Option */}
                <Box>
                  <Checkbox
                    isChecked={resendSplitMessage}
                    onChange={(e) => setResendSplitMessage(e.target.checked)}
                    colorScheme="blue"
                  >
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        Split long message
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Split messages longer than 160 characters into multiple
                        SMS parts
                      </Text>
                    </VStack>
                  </Checkbox>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onResendModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isLoading={sendingMessage}
              onClick={() => {
                if (resendMessageData?.message) {
                  resendMessage(resendMessageData.message, resendSplitMessage);
                  onResendModalClose();
                  setResendMessageData(null);
                }
              }}
            >
              Resend Message
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Modal>
  );
};

export default PatientMessagingModal;
